import { get } from 'lodash/fp';
import { MetaGroupTypes, MetaGroupRoles } from '@whitelabel/helpers/constants';
import { IAddressFieldInfo, IProcessFieldResults, ISectionField } from '@whitelabel/helpers/types';
import { enumKeys } from '@whitelabel/helpers/utils';
import { sliceArrayIntoChunks, updateClaimantKeys } from './utils';

export const getAddressFieldInfo = (addressFields: ISectionField[]) => {
  const allAddressFieldData = updateClaimantKeys<IAddressFieldInfo<ISectionField>>(
    addressFields.reduce((acc, field) => {
      const role = field.meta?.group?.role;
      if (!!role && Object.values(MetaGroupRoles).includes(role)) {
        return { ...acc, [role]: field };
      }
      return acc;
    }, {} as Record<MetaGroupRoles, ISectionField>),
    true,
  );

  function mapAddressValue<T>(property: string) {
    return Object.keys(allAddressFieldData).reduce(
      (acc, key) => ({
        ...acc,
        [key]: get(property, allAddressFieldData[key as keyof IAddressFieldInfo<ISectionField>]) as T,
      }),
      {} as T,
    );
  }

  const nameList = mapAddressValue<IAddressFieldInfo<string>>('name');
  const labelList = mapAddressValue<IAddressFieldInfo<string | undefined>>('meta.label');
  const descriptionList = mapAddressValue<IAddressFieldInfo<string | undefined>>('meta.description');
  const placeholderList = mapAddressValue<IAddressFieldInfo<string | undefined>>('meta.placeholder');
  const requiredList = mapAddressValue<IAddressFieldInfo<boolean>>('required');

  return {
    address1Field: allAddressFieldData.address1,
    nameList,
    labelList,
    descriptionList,
    placeholderList,
    requiredList,
  };
};

export const processFieldRoles = (sectionFields: ISectionField[]): IProcessFieldResults[] => {
  const processedFieldsArr: IProcessFieldResults[] = [];
  for (const value of enumKeys(MetaGroupTypes)) {
    const groupFields = sectionFields.filter(
      (sectionField) => sectionField.meta?.group?.type === MetaGroupTypes[value],
    );
    if (groupFields.length) {
      processedFieldsArr.push({ type: MetaGroupTypes[value], groupFields });
    }
  }
  return processedFieldsArr;
};

export const processSectionFields = (
  sectionFields: ISectionField[],
  { type, groupFields }: IProcessFieldResults,
): ISectionField[] => {
  switch (type) {
    case 'address': {
      const nonAddressFields = sectionFields.filter(
        (sectionField) => sectionField.meta?.group?.type !== MetaGroupTypes.ADDRESS,
      );

      const chunkedAddress = sliceArrayIntoChunks(groupFields, 6);

      const groupedAddresses = chunkedAddress.map((address) => {
        const { address1Field, nameList, labelList, descriptionList, placeholderList, requiredList } =
          getAddressFieldInfo(address);

        const groupedAddress: ISectionField = {
          conditions: null,
          id: address1Field.id,
          meta: { label: address1Field.meta?.label as string },
          name: address1Field.meta?.group?.name as string,
          options: null,
          ordering: address1Field.ordering,
          readOnly: false,
          required: false,
          type: 'FNOLAddress',
          validation: null,
          nameList,
          labelList,
          descriptionList,
          placeholderList,
          requiredList,
        };
        return groupedAddress;
      });

      return nonAddressFields.concat(groupedAddresses);
    }

    case 'currency_amount': {
      const nonCurrencyAmountFields = sectionFields.filter(
        (sectionField) => sectionField.meta?.group?.type !== MetaGroupTypes.CURRENCY_AMOUNT,
      );

      const chunkedCurrencyAmount = sliceArrayIntoChunks(groupFields, 2);

      const groupedCurrencyAmounts = chunkedCurrencyAmount.map((currencyAmount) => {
        const amountField = currencyAmount.find((field) => field.meta?.group?.role === MetaGroupRoles.AMOUNT)!;
        const currencyField = currencyAmount.find((field) => field.meta?.group?.role === MetaGroupRoles.CURRENCY)!;

        const groupedCurrencyAmount: ISectionField = {
          conditions: null,
          id: amountField.id,
          meta: {
            label: amountField.meta?.label as string,
            description: amountField.meta?.description,
            placeholder: amountField.meta?.placeholder,
          },
          name: amountField.name,
          options: null,
          ordering: amountField.ordering,
          readOnly: amountField.readOnly,
          required: amountField.required,
          type: 'currencyAmount',
          validation: amountField.validation,
          bundledCurrencyFieldName: currencyField.name,
        };
        return groupedCurrencyAmount;
      });

      return nonCurrencyAmountFields.concat(groupedCurrencyAmounts);
    }

    case 'table':
    default:
      return sectionFields;
  }
};
